"use client"

import { useState } from "react"
import { Box, Button, TextField, Typography, InputAdornment, IconButton, useMediaQuery, useTheme } from "@mui/material"
import { Mail, Lock, Eye, EyeOff } from "lucide-react"
import { useDispatch, useSelector } from "react-redux"
import { loginAsync, selectError } from "../../store/slices/authSlice"
import { useNavigate } from "react-router"

export default function Login() {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"))

  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)

  const navigate = useNavigate()
  const error = useSelector(selectError)
  const dispatch = useDispatch()

  const handleSubmit = async (e) => {
    e.preventDefault()
    await dispatch(loginAsync({ email, password }))
    const storedToken = localStorage.getItem("token")
    if (storedToken) {
      navigate("/dashboard")
    }
  }

  return (
    <Box
      dir="ltr"
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        minHeight: "100vh",
        width: "100%",
        backgroundColor: "#FFF9F3",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Logo for mobile view */}
      {isMobile && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            padding: "20px 0",
            width: "100%",
            zIndex: 2,
          }}
        >
          <img
            alt="BASK Hospitality"
            src="/assets/logo.png"
            style={{
              width: isSmallScreen ? "300px" : "300px",
              height: "400px",


               
              objectFit: "contain",
            }}
          />
        </Box>
      )}

      {/* Left side with image - hidden on mobile */}
      <Box
        sx={{
          width: { xs: "100%", md: "50%" },
          display: { xs: "none", md: "block" },
          position: "relative",
          height: { md: "100vh" },
        }}
      >
        <img
          alt="BASK Hospitality"
          src="/assets/login.png"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />

        {/* Logo overlay on the image for desktop */}
        <Box
          sx={{
            display:isSmallScreen?"none":"block",
            position: "absolute",
            top: "20px",
            left: "1600px",
            zIndex: 2,
          }}
        >
          <img
            alt="BASK Hospitality"
            src="/assets/logo.png"
            style={{
              width: "300px",
              height: "400px",
            }}
          />
        </Box>
      </Box>

      {/* Right side with form */}
      <Box
        sx={{
          width: { xs: "100%", md: "50%" },
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          padding: { xs: "20px", sm: "30px", md: "40px", lg: "60px" },
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            maxWidth: "450px",
            width: "100%",
            margin: "0 auto",
          }}
        >
          <Typography
            variant="h4"
            sx={{
              color: "#C47D60",
              fontWeight: "500",
              marginBottom: { xs: "20px", sm: "30px" },
              fontSize: { xs: "24px", sm: "28px", md: "34px" },
              textAlign: "center",
            }}
          >
            Welcome Back!
          </Typography>

          <form onSubmit={handleSubmit}>
            {/* Email Field */}
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: "900",
                marginBottom: "8px",
                color: "#555",
              }}
            >
              Email
            </Typography>
            <TextField
              variant="outlined"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Mail size={18} color="black" />
                  </InputAdornment>
                ),
                style: {
                  backgroundColor: "white",
                  borderRadius: "8px",
                  fontSize: "14px",
                  padding: "2px 4px",
                },
              }}
              sx={{
                marginTop: "0",
                marginBottom: "20px",
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#E0E0E0" },
                  "&:hover fieldset": { borderColor: "#C47D60" },
                  "&.Mui-focused fieldset": { borderColor: "#C47D60" },
                },
              }}
            />

            {/* Password Field */}
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: "900",
                marginBottom: "8px",
                color: "#555",
              }}
            >
              Password
            </Typography>
            <TextField
              type={showPassword ? "text" : "password"}
              variant="outlined"
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock size={18} color="black" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" sx={{ mr: 1 }}>
                      {showPassword ? <EyeOff size={18} color="#C47D60" /> : <Eye size={18} color="#C47D60" />}
                    </IconButton>
                  </InputAdornment>
                ),
                style: {
                  backgroundColor: "white",
                  borderRadius: "8px",
                  fontSize: "14px",
                  padding: "2px 4px",
                },
              }}
              sx={{
                marginTop: "0",
                marginBottom: { xs: "20px", sm: "30px" },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#E0E0E0" },
                  "&:hover fieldset": { borderColor: "#C47D60" },
                  "&.Mui-focused fieldset": { borderColor: "#C47D60" },
                },
              }}
            />

            {/* Login Button */}
            <Button
              type="submit"
              variant="contained"
              fullWidth
              sx={{
                backgroundColor: "#C47D60",
                color: "white",
                padding: { xs: "10px", sm: "12px" },
                fontSize: { xs: "14px", sm: "16px" },
                fontWeight: "500",
                textTransform: "none",
                borderRadius: "8px",
                boxShadow: "none",
                "&:hover": { backgroundColor: "#A65A42", boxShadow: "none" },
              }}
            >
              Login
            </Button>

            {error && (
              <Typography
                sx={{
                  color: "red",
                  textAlign: "center",
                  marginTop: "10px",
                  fontSize: { xs: "12px", sm: "14px" },
                }}
              >
                {error}
              </Typography>
            )}
          </form>
        </Box>
      </Box>
    </Box>
  )
}
